import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import {
  LuCalendarClock,
  LuDownload,
  LuPencil,
  LuPlay,
  LuPlus,
  LuTrash2,
} from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { useDebounce } from '../../hooks/useDebounce';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { formatDate } from '../../utils/dateTimeFormat';
import { refreshNotifications } from '../../utils/notificationEvents';
import CreateTestPlanModal from '../test-plans/CreateTestPlanModal';
import ViewTestPlanModal from '../test-plans/ViewTestPlanModal';
import RunTestPlanModal from '../test-plans/RunTestPlanModal';
import ScheduleTestPlanModal from '../test-plans/ScheduleTestPlanModal';
import LinkTestSuitesModal from '../test-plans/LinkTestSuitesModal';
import SprintModal from '../test-plans/SprintModal';
import ExecutionHistoryModal from '../common/ExecutionHistoryModal';
import { parseTestPlanApiFieldErrors } from '../test-plans/testExecutionValidation';
import {
  BulkActionBar,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  MetricCard,
  Pagination,
  PriorityBadge,
  RowActions,
  SearchInput,
  StatusBadge,
} from '../ui';
import EntitySearchSelect from './EntitySearchSelect';
import '../../styles/rt2-plans-entity.css';

const ENTITY_CONFIG = {
  releases: {
    key: 'releases',
    singular: 'release',
    label: 'Release',
    labels: 'Releases',
    typeId: 1,
    entityType: 'release',
    activeTab: 'releases',
    pageSize: 25,
    planPageSize: 10,
    /** Releases list API is page-based; search is client-side over loaded pages. */
    supportsServerSearch: false,
    getId: (e) => e.release_id ?? e.id,
    getName: (e) => e.name || 'Untitled release',
    getMeta: (e) =>
      [e.start_date && `Start ${formatDate(e.start_date)}`, e.end_date && `End ${formatDate(e.end_date)}`]
        .filter(Boolean)
        .join(' · '),
    fetch: async (projectId, page, pageSize, _search = '') => {
      const response = await axiosInstance.get(
        `/release/releases?project_id=${projectId}&page=${page}&page_size=${pageSize}`
      );
      return {
        items: response?.results || [],
        total: response?.total || 0,
      };
    },
    create: (projectId, data) =>
      axiosInstance.post(
        '/release/releases',
        {
          project_id: projectId,
          release_id: data.release_id,
          name: data.name,
          description: data.description,
          status: data.status,
          start_date: data.start_date,
          end_date: data.end_date,
        },
        { skipGlobalToast: true }
      ),
    update: (data) =>
      axiosInstance.put(`/release/releases/${data.release_id}`, {
        name: data.name,
        description: data.description,
        status: data.status,
        start_date: data.start_date,
        end_date: data.end_date,
      }),
    remove: (entity) => axiosInstance.delete(`/release/releases/${entity.release_id ?? entity.id}`),
    planEndpoint: '/test_plan/test-plans/release',
    planPayloadKey: 'release_id',
  },
  sprints: {
    key: 'sprints',
    singular: 'sprint',
    label: 'Sprint',
    labels: 'Sprints',
    typeId: 2,
    entityType: 'sprint',
    activeTab: 'sprints',
    pageSize: 25,
    planPageSize: 10,
    supportsServerSearch: false,
    getId: (e) => e.sprint_id ?? e.id,
    getName: (e) => e.name || 'Untitled sprint',
    getMeta: (e) =>
      [e.start_date && `Start ${formatDate(e.start_date)}`, e.end_date && `End ${formatDate(e.end_date)}`]
        .filter(Boolean)
        .join(' · '),
    fetch: async (projectId, page, pageSize, _search = '') => {
      const response = await axiosInstance.get(
        `/sprints/list/sprints?project_id=${projectId}&page=${page}&page_size=${pageSize}`
      );
      return {
        items: response?.sprints || [],
        total: response?.total_count || 0,
      };
    },
    create: (projectId, data) =>
      axiosInstance.post('/sprints/sprint', {
        project_id: projectId,
        name: data.name,
        description: data.description,
        status: data.status || 'planned',
        start_date: data.start_date,
        end_date: data.end_date,
      }),
    update: (data) =>
      axiosInstance.put(`/sprints/sprint/${data.sprint_id}?project_id=${data.project_id}`, {
        name: data.name,
        description: data.description,
        status: data.status,
        start_date: data.start_date,
        end_date: data.end_date,
      }),
    remove: (entity, projectId) =>
      axiosInstance.delete(`/sprints/sprint/${entity.sprint_id ?? entity.id}?project_id=${projectId}`),
    planEndpoint: '/test_plan/test-plans/sprint',
    planPayloadKey: 'sprint_id',
  },
  epics: {
    key: 'epics',
    singular: 'epic',
    label: 'Epic',
    labels: 'Epics',
    typeId: 3,
    entityType: 'epic',
    activeTab: 'epics',
    pageSize: 25,
    planPageSize: 10,
    supportsServerSearch: true,
    getId: (e) => e.id,
    getName: (e) => e.summary || e.name || e.key || 'Untitled epic',
    getMeta: (e) =>
      [e.key, e.priority && `Priority ${e.priority}`, e.assignee && `Owner ${e.assignee}`]
        .filter(Boolean)
        .join(' · '),
    fetch: async (projectId, page, pageSize, search = '') => {
      const params = new URLSearchParams({
        project_id: String(projectId),
        page: String(page),
        page_size: String(pageSize),
      });
      if (search.trim()) params.set('search', search.trim());
      const response = await axiosInstance.get(`/epics/list?${params.toString()}`);
      const items = (response?.epics || []).map((epic) => ({
        id: epic.id,
        key: epic.key,
        name: epic.summary || epic.name,
        summary: epic.summary,
        description: epic.description,
        status: epic.status,
        priority: epic.priority,
        assignee: epic.assignee,
        story_point: epic.story_point,
        projectId: epic.project_id,
      }));
      return {
        items,
        total: response?.total_epic_count || response?.total_count || items.length,
      };
    },
    create: (projectId, data) =>
      axiosInstance.post('/epics/create', {
        key: `EPIC-${Math.floor(Math.random() * 1000)}`,
        project_id: projectId,
        summary: data.summary,
        name: data.name || data.summary,
        description: data.description || '',
        status: data.status || 'To Do',
        priority: data.priority || 'medium',
        assignee: data.assignee || '',
        story_point: Number(data.story_point) || 0,
      }),
    update: (data) =>
      axiosInstance.put(`/epics/${data.epic_id}`, {
        summary: data.summary,
        name: data.name || data.summary,
        description: data.description,
        status: data.status,
        priority: data.priority,
        assignee: data.assignee,
        story_point: data.story_point,
      }),
    remove: (entity, projectId) =>
      axiosInstance.delete(`/epics/${entity.id}?project_id=${projectId}`),
    planEndpoint: '/test_plan/epic_plan',
    planPayloadKey: 'epic_id',
  },
};

const transformPlan = (plan) => ({
  test_plan_id: plan.test_plan_id,
  id: plan.test_plan_id,
  name: plan.name,
  description: plan.description,
  status: plan.status || 'draft',
  priority: plan.priority || 'medium',
  assignee: plan.assignee || 'Unassigned',
  assignee_id: plan.assignee_id || null,
  duration: plan.duration || 0,
  created_at: plan.created_at,
  updated_at: plan.updated_at,
  testCases: plan?.test_case_count || 0,
  testSuites: plan.linked_suites
    ? plan.linked_suites.map((suite) => ({
        suite_id: suite.suite_id,
        suite_name: suite.suite_name,
      }))
    : [],
  projectId: plan.project_id,
  typeId: plan.type_id,
  referenceId: plan.reference_id,
  environment_setting_id: plan.environment_setting_id ?? null,
});

/**
 * Release / Sprint / Epic workspace — full-width plans table
 * with searchable entity selector (no permanent left rail).
 */
const PlansEntityWorkspace = ({
  tab = 'releases',
  projectId,
  projectName = 'Project',
}) => {
  const config = ENTITY_CONFIG[tab] || ENTITY_CONFIG.releases;
  const navigate = useNavigate();
  const { getWorkspaceIdFromURL } = useWorkspaceRouting();

  const [entities, setEntities] = useState([]);
  const [entityTotal, setEntityTotal] = useState(0);
  const [entityPage, setEntityPage] = useState(1);
  const entityPageSize = config.pageSize;
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitiesLoadingMore, setEntitiesLoadingMore] = useState(false);
  const [entitiesSearching, setEntitiesSearching] = useState(false);
  const [entitiesError, setEntitiesError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedEntityCache, setSelectedEntityCache] = useState(null);
  const [entitySearchInput, setEntitySearchInput] = useState('');
  const debouncedEntitySearch = useDebounce(entitySearchInput, 350);
  const loadMoreLockRef = useRef(false);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState(null);
  const [planQuery, setPlanQuery] = useState('');
  const [planPage, setPlanPage] = useState(1);
  const [planPageSize, setPlanPageSize] = useState(config.planPageSize);
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);

  const [environments, setEnvironments] = useState([]);
  const [members, setMembers] = useState([]);

  const [entityModalOpen, setEntityModalOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [entitySubmitting, setEntitySubmitting] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState(null);
  const [deletingEntity, setDeletingEntity] = useState(false);

  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [viewPlan, setViewPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(false);
  const [runPlans, setRunPlans] = useState(null);
  const [schedulePlan, setSchedulePlan] = useState(null);
  const [linkPlan, setLinkPlan] = useState(null);
  const [linkMode, setLinkMode] = useState('link');
  const [historyPlan, setHistoryPlan] = useState(null);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [exporting, setExporting] = useState(false);

  const selectedEntity = useMemo(() => {
    const fromList = entities.find((e) => String(config.getId(e)) === String(selectedId));
    if (fromList) return fromList;
    if (
      selectedEntityCache &&
      String(config.getId(selectedEntityCache)) === String(selectedId)
    ) {
      return selectedEntityCache;
    }
    return null;
  }, [entities, selectedId, config, selectedEntityCache]);

  const entityOptions = useMemo(
    () =>
      entities.map((e) => {
        const id = String(config.getId(e));
        const name = config.getName(e);
        const meta = config.getMeta(e);
        return {
          value: id,
          label: name,
          meta: meta || undefined,
        };
      }),
    [entities, config]
  );

  const selectedEntityOption = useMemo(() => {
    if (!selectedEntity) return null;
    return {
      value: String(config.getId(selectedEntity)),
      label: config.getName(selectedEntity),
      meta: config.getMeta(selectedEntity) || undefined,
    };
  }, [selectedEntity, config]);

  const hasMoreEntities = entities.length < entityTotal;

  const planMetrics = useMemo(() => {
    const suiteCount = plans.reduce((n, p) => n + (p.testSuites?.length || 0), 0);
    const caseCount = plans.reduce((n, p) => n + (Number(p.testCases) || 0), 0);
    const activeCount = plans.filter((p) =>
      ['active', 'in progress', 'in_progress'].includes(String(p.status || '').toLowerCase())
    ).length;
    const draftCount = plans.filter((p) => String(p.status || '').toLowerCase() === 'draft').length;
    return { suiteCount, caseCount, activeCount, draftCount };
  }, [plans]);

  const filteredPlans = useMemo(() => {
    const q = planQuery.trim().toLowerCase();
    if (!q) return plans;
    return plans.filter(
      (p) =>
        String(p.name || '').toLowerCase().includes(q) ||
        String(p.status || '').toLowerCase().includes(q) ||
        String(p.assignee || '').toLowerCase().includes(q) ||
        String(p.description || '').toLowerCase().includes(q)
    );
  }, [plans, planQuery]);

  const pagedPlans = useMemo(() => {
    const start = (planPage - 1) * planPageSize;
    return filteredPlans.slice(start, start + planPageSize);
  }, [filteredPlans, planPage, planPageSize]);

  const navigateTo = useCallback(
    (path) => {
      const workspaceId = getWorkspaceIdFromURL();
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
      } else {
        navigate(`/dashboard/${path}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const loadMeta = useCallback(async () => {
    if (!projectId) return;
    try {
      const [envRes, memRes] = await Promise.all([
        axiosInstance.get(`/user_profile/list_environments?project_id=${projectId}`),
        axiosInstance.get(`/projects/list_project_members?project_id=${projectId}`),
      ]);
      setEnvironments(envRes?.data || envRes || []);
      setMembers(memRes?.data || memRes?.members || memRes || []);
    } catch (err) {
      console.error(err);
    }
  }, [projectId]);

  const loadEntities = useCallback(
    async ({
      page = 1,
      append = false,
      keepSelection = true,
      search = '',
      soft = false,
    } = {}) => {
      if (!projectId) {
        setEntities([]);
        setEntityTotal(0);
        setSelectedId(null);
        setSelectedEntityCache(null);
        setEntitiesLoading(false);
        setEntitiesError('Select a project to continue.');
        return;
      }

      if (append) setEntitiesLoadingMore(true);
      else if (soft) setEntitiesSearching(true);
      else setEntitiesLoading(true);
      setEntitiesError(null);

      try {
        const serverSearch = config.supportsServerSearch ? search : '';
        const { items, total } = await config.fetch(
          projectId,
          page,
          entityPageSize,
          serverSearch
        );
        const nextItems = items || [];
        setEntityTotal(Number(total) || nextItems.length);
        setEntityPage(page);
        setEntities((prev) => {
          if (!append) return nextItems;
          const seen = new Set(prev.map((e) => String(config.getId(e))));
          return prev.concat(nextItems.filter((e) => !seen.has(String(config.getId(e)))));
        });

        setSelectedId((prev) => {
          if (keepSelection && prev) return prev;
          return nextItems[0] ? String(config.getId(nextItems[0])) : null;
        });

        if (!keepSelection) {
          setSelectedEntityCache(nextItems[0] || null);
        }
      } catch (err) {
        console.error(err);
        if (!append) {
          setEntities([]);
          setEntityTotal(0);
          if (!keepSelection) {
            setSelectedId(null);
            setSelectedEntityCache(null);
          }
        }
        setEntitiesError(err?.message || `Failed to load ${config.labels.toLowerCase()}`);
      } finally {
        setEntitiesLoading(false);
        setEntitiesLoadingMore(false);
        setEntitiesSearching(false);
        loadMoreLockRef.current = false;
      }
    },
    [projectId, config, entityPageSize]
  );

  const loadMoreEntities = useCallback(() => {
    if (loadMoreLockRef.current || entitiesLoadingMore || entitiesLoading) return;
    if (entities.length >= entityTotal) return;
    loadMoreLockRef.current = true;
    loadEntities({
      page: entityPage + 1,
      append: true,
      keepSelection: true,
      search: debouncedEntitySearch,
      soft: true,
    });
  }, [
    entitiesLoadingMore,
    entitiesLoading,
    entities.length,
    entityTotal,
    entityPage,
    loadEntities,
    debouncedEntitySearch,
  ]);

  const loadPlans = useCallback(async () => {
    if (!projectId || !selectedEntity) {
      setPlans([]);
      setPlansLoading(false);
      setPlansError(null);
      setSelectedPlanIds([]);
      return;
    }
    setPlansLoading(true);
    setPlansError(null);
    try {
      const referenceId = config.getId(selectedEntity);
      const url = `/test_plan/test-plans/all_type?project_id=${projectId}&type=${config.typeId}&reference_id=${referenceId}`;
      const response = await axiosInstance.get(url);
      const raw = response?.test_plans || [];
      setPlans((Array.isArray(raw) ? raw : []).map(transformPlan));
      setPlanPage(1);
      setSelectedPlanIds([]);
    } catch (err) {
      console.error(err);
      setPlans([]);
      setPlansError(err?.message || 'Failed to load plans');
    } finally {
      setPlansLoading(false);
    }
  }, [projectId, selectedEntity, config]);

  useEffect(() => {
    setPlanQuery('');
    setPlanPage(1);
    setSelectedPlanIds([]);
    setSelectedId(null);
    setSelectedEntityCache(null);
    setEntitySearchInput('');
    setEntityPage(1);
    loadEntities({ page: 1, keepSelection: false, search: '' });
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, projectId]);

  // Server-side search (epics) — refetch page 1 when debounced query changes.
  const serverSearchReadyRef = useRef(false);
  const prevServerSearchRef = useRef('');
  useEffect(() => {
    serverSearchReadyRef.current = false;
    prevServerSearchRef.current = '';
  }, [tab, projectId]);
  useEffect(() => {
    if (!config.supportsServerSearch || !projectId) return;
    if (!serverSearchReadyRef.current) {
      serverSearchReadyRef.current = true;
      prevServerSearchRef.current = debouncedEntitySearch;
      return;
    }
    if (prevServerSearchRef.current === debouncedEntitySearch) return;
    prevServerSearchRef.current = debouncedEntitySearch;
    loadEntities({
      page: 1,
      keepSelection: true,
      search: debouncedEntitySearch,
      soft: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEntitySearch, config.supportsServerSearch]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    setPlanPage(1);
  }, [planQuery]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedEntityCache(null);
      return;
    }
    const match = entities.find((e) => String(config.getId(e)) === String(selectedId));
    if (match) setSelectedEntityCache(match);
  }, [selectedId, entities, config]);

  const openCreateEntity = () => {
    setEditingEntity(null);
    setEntityModalOpen(true);
  };

  const openEditEntity = (entity, e) => {
    e?.stopPropagation?.();
    setEditingEntity(entity);
    setEntityModalOpen(true);
  };

  const handleEntitySubmit = async (formData) => {
    setEntitySubmitting(true);
    try {
      if (editingEntity) {
        await config.update(formData);
        toast.success(`${config.label} updated`);
      } else {
        await config.create(projectId, formData);
        toast.success(`${config.label} created`);
      }
      setEntityModalOpen(false);
      setEditingEntity(null);
      await loadEntities({
        page: 1,
        keepSelection: Boolean(editingEntity),
        search: config.supportsServerSearch ? debouncedEntitySearch : '',
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || `Failed to save ${config.singular}`);
      throw err;
    } finally {
      setEntitySubmitting(false);
    }
  };

  const confirmDeleteEntity = async () => {
    if (!entityToDelete) return;
    setDeletingEntity(true);
    try {
      await config.remove(entityToDelete, projectId);
      toast.success(`${config.label} deleted`);
      setEntityToDelete(null);
      setEntitySearchInput('');
      await loadEntities({ page: 1, keepSelection: false, search: '' });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || `Failed to delete ${config.singular}`);
    } finally {
      setDeletingEntity(false);
    }
  };

  const handleCreatePlan = async (payload) => {
    const referenceId = config.getId(selectedEntity);
    const body = {
      project_id: projectId,
      name: payload.name,
      description: payload.description,
      status: payload.status || 'draft',
      priority: payload.priority,
      assignee: payload.assignee,
      duration: payload.duration ?? payload.estimatedDuration ?? 0,
      environment_setting_id: payload.environment_setting_id || null,
      [config.planPayloadKey]: referenceId,
    };
    try {
      await axiosInstance.post(config.planEndpoint, body);
      setCreatePlanOpen(false);
      toast.success('Plan created');
      await loadPlans();
    } catch (err) {
      const fieldErrors = parseTestPlanApiFieldErrors(err?.response?.data);
      if (Object.keys(fieldErrors).length) {
        throw Object.assign(new Error('Validation failed'), { fieldErrors });
      }
      toast.error(err?.response?.data?.message || err?.message || 'Failed to create plan');
      throw err;
    }
  };

  const handleEditPlan = async (testPlanId, updatedData) => {
    if (!updatedData?.assignee) {
      throw Object.assign(new Error('Assignee is required'), {
        fieldErrors: { assignee: 'Assignee is required' },
      });
    }
    const payload = {
      name: updatedData.name || '',
      description: updatedData.description || '',
      status: updatedData.status || 'draft',
      priority: updatedData.priority || 'medium',
      duration: updatedData.duration || 0,
      assignee_id: parseInt(updatedData.assignee, 10),
    };
    if (Number.isNaN(payload.assignee_id)) {
      throw Object.assign(new Error('Assignee is required'), {
        fieldErrors: { assignee: 'Assignee is required' },
      });
    }
    try {
      await axiosInstance.put(`/test_plan/update/${testPlanId}`, payload);
      refreshNotifications();
      await loadPlans();
      toast.success('Plan updated');
    } catch (err) {
      const fieldErrors = parseTestPlanApiFieldErrors(err?.response?.data);
      if (Object.keys(fieldErrors).length) {
        throw Object.assign(new Error('Validation failed'), { fieldErrors });
      }
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update plan');
      throw err;
    }
  };

  const confirmDeletePlan = async () => {
    if (!planToDelete) return;
    setDeletingPlan(true);
    try {
      await axiosInstance.delete(`/test_plan/delete/${planToDelete.test_plan_id || planToDelete.id}`);
      toast.success('Plan deleted');
      setPlanToDelete(null);
      setViewPlan(null);
      await loadPlans();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete plan');
    } finally {
      setDeletingPlan(false);
    }
  };

  const togglePlanSelect = (planId, checked) => {
    setSelectedPlanIds((prev) =>
      checked ? [...new Set([...prev, planId])] : prev.filter((id) => id !== planId)
    );
  };

  const toggleSelectAllVisible = (checked) => {
    const visibleIds = pagedPlans.map((p) => p.test_plan_id);
    setSelectedPlanIds((prev) => {
      if (checked) return [...new Set([...prev, ...visibleIds])];
      return prev.filter((id) => !visibleIds.includes(id));
    });
  };

  const openRunForPlans = (planList) => {
    if (!planList.length) {
      toast.warning('Select at least one plan to run');
      return;
    }
    setRunPlans({
      ids: planList.map((p) => p.test_plan_id || p.id),
      names: planList.map((p) => p.name || `Plan ${p.test_plan_id}`),
      environmentSettingId:
        planList.find((p) => p.environment_setting_id != null)?.environment_setting_id ?? null,
    });
  };

  const handleExport = async () => {
    if (!projectId) return;
    setExporting(true);
    try {
      const params = { project_id: projectId, type_id: config.typeId };
      if (selectedEntity) {
        params.reference_id = config.getId(selectedEntity);
      }
      const blobData = await axiosInstance.get('/test_plan/export_test_plans', {
        params,
        responseType: 'blob',
      });
      const blob = blobData instanceof Blob ? blobData : new Blob([blobData]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute(
        'download',
        `${config.singular}-plans-${new Date().toISOString().split('T')[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Export started');
    } catch (err) {
      console.error(err);
      toast.error('Failed to export plans');
    } finally {
      setExporting(false);
    }
  };

  const allVisibleSelected =
    pagedPlans.length > 0 && pagedPlans.every((p) => selectedPlanIds.includes(p.test_plan_id));
  const someVisibleSelected =
    pagedPlans.some((p) => selectedPlanIds.includes(p.test_plan_id)) && !allVisibleSelected;

  const columns = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allVisibleSelected}
          ref={(el) => {
            if (el) el.indeterminate = someVisibleSelected;
          }}
          onChange={(e) => toggleSelectAllVisible(e.target.checked)}
          aria-label="Select all visible plans"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: 44,
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedPlanIds.includes(row.test_plan_id)}
          onChange={(e) => togglePlanSelect(row.test_plan_id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.name}`}
        />
      ),
    },
    {
      key: 'name',
      header: 'Plan',
      render: (row) => (
        <div>
          <button
            type="button"
            className="rt2-link"
            onClick={(e) => {
              e.stopPropagation();
              navigateTo(`test-plans/${row.test_plan_id}`);
            }}
          >
            {row.name}
          </button>
          {row.description ? (
            <div className="rt2-muted" style={{ marginTop: 2 }}>
              {String(row.description).slice(0, 64)}
              {String(row.description).length > 64 ? '…' : ''}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <PriorityBadge priority={row.priority} />,
    },
    {
      key: 'cases',
      header: 'Cases',
      render: (row) => row.testCases ?? 0,
    },
    {
      key: 'suites',
      header: 'Suites',
      render: (row) => row.testSuites?.length ?? 0,
    },
    {
      key: 'owner',
      header: 'Owner',
      render: (row) => row.assignee || '—',
    },
    {
      key: 'updated',
      header: 'Updated',
      render: (row) => formatDate(row.updated_at, '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: 150,
      render: (row) => {
        const completed = String(row.status).toLowerCase() === 'completed';
        return (
          <RowActions
            primary={
              <button
                type="button"
                className="rt-btn rt-btn--secondary rt-btn--sm"
                onClick={() => openRunForPlans([row])}
                title="Run plan"
              >
                <LuPlay size={14} aria-hidden /> Run
              </button>
            }
            menuItems={[
              {
                id: 'view',
                label: 'View',
                onClick: () => {
                  setViewPlan(row);
                  setEditingPlan(false);
                },
              },
              {
                id: 'edit',
                label: 'Edit',
                disabled: completed,
                onClick: () => {
                  setViewPlan(row);
                  setEditingPlan(true);
                },
              },
              {
                id: 'schedule',
                label: 'Schedule',
                disabled: completed,
                onClick: () => setSchedulePlan(row),
              },
              {
                id: 'link',
                label: 'Link suites',
                disabled: completed,
                onClick: () => {
                  setLinkMode('link');
                  setLinkPlan(row);
                },
              },
              {
                id: 'unlink',
                label: 'Unlink suites',
                disabled: completed || !row.testSuites?.length,
                onClick: () => {
                  setLinkMode('unlink');
                  setLinkPlan(row);
                },
              },
              {
                id: 'history',
                label: 'Execution history',
                onClick: () => setHistoryPlan(row),
              },
              {
                id: 'open',
                label: 'Open detail',
                onClick: () => navigateTo(`test-plans/${row.test_plan_id}`),
              },
              {
                id: 'delete',
                label: 'Delete',
                danger: true,
                dividerBefore: true,
                disabled: completed,
                onClick: () => setPlanToDelete(row),
              },
            ]}
          />
        );
      },
    },
  ];

  if (!projectId) {
    return (
      <ErrorState
        title="Select a project"
        description={`Choose a project to manage ${config.labels.toLowerCase()} and plans.`}
      />
    );
  }

  return (
    <div className="rt-plans-fw">
      {/* Entity switcher bar */}
      <div className="rt-plans-fw__switcher">
        <div className="rt-plans-fw__switcher-left">
          <label className="rt-plans-fw__switcher-label" htmlFor={`entity-select-${config.key}`}>
            {config.label}
          </label>
          {entitiesLoading ? (
            <div className="rt-plans-fw__switcher-loading">Loading {config.labels.toLowerCase()}…</div>
          ) : entitiesError && entities.length === 0 && !selectedEntity ? (
            <ErrorState
              title={`Unable to load ${config.labels.toLowerCase()}`}
              description={entitiesError}
              onRetry={() => loadEntities({ page: 1, keepSelection: false, search: '' })}
            />
          ) : entityTotal === 0 && !entitySearchInput.trim() ? (
            <div className="rt-plans-fw__switcher-empty">
              No {config.labels.toLowerCase()} yet.
              <button type="button" className="rt-btn rt-btn--primary rt-btn--sm" onClick={openCreateEntity}>
                <LuPlus size={14} aria-hidden /> Create {config.label}
              </button>
            </div>
          ) : (
            <EntitySearchSelect
              id={`entity-select-${config.key}`}
              className="rt-plans-fw__entity-select"
              value={selectedId ? String(selectedId) : ''}
              onChange={(next) => setSelectedId(next || null)}
              options={entityOptions}
              selectedOption={selectedEntityOption}
              aria-label={`Select ${config.singular}`}
              placeholder={`Select a ${config.singular}…`}
              searchPlaceholder={`Search ${config.labels.toLowerCase()}…`}
              onSearchChange={
                config.supportsServerSearch ? setEntitySearchInput : undefined
              }
              hasMore={hasMoreEntities}
              loadingMore={entitiesLoadingMore}
              searching={entitiesSearching}
              onLoadMore={loadMoreEntities}
              footerHint={
                hasMoreEntities
                  ? `Showing ${entities.length} of ${entityTotal} — scroll or search to find more`
                  : entityTotal > 0
                    ? `${entityTotal} total`
                    : null
              }
            />
          )}
          {!entitiesLoading && entityTotal > 0 && (
            <span className="rt-plans-fw__entity-count">
              {entities.length >= entityTotal
                ? `${entityTotal} total`
                : `${entities.length} of ${entityTotal}`}
            </span>
          )}
        </div>
        <div className="rt-plans-fw__switcher-actions">
          <button
            type="button"
            className="rt-btn rt-btn--secondary rt-btn--sm"
            onClick={openCreateEntity}
          >
            <LuPlus size={14} aria-hidden /> New {config.label}
          </button>
          {selectedEntity && (
            <button
              type="button"
              className="rt-btn rt-btn--secondary rt-btn--sm"
              onClick={() => setEntityToDelete(selectedEntity)}
              aria-label={`Delete ${config.getName(selectedEntity)}`}
            >
              <LuTrash2 size={14} aria-hidden /> Delete
            </button>
          )}
        </div>
      </div>

      {!selectedEntity && !entitiesLoading && entities.length > 0 && (
        <div className="rt-plans-fw__empty">
          <EmptyState
            title={`Select a ${config.singular}`}
            description={`Choose a ${config.singular} above to manage its test plans.`}
          />
        </div>
      )}

      {selectedEntity && (
        <>
          {/* Selected entity header */}
          <header className="rt-plans-fw__entity-header">
            <div className="rt-plans-fw__entity-header-main">
              <div className="rt-plans-fw__entity-title-row">
                <h3 className="rt-plans-fw__entity-title" title={config.getName(selectedEntity)}>
                  {config.getName(selectedEntity)}
                </h3>
                {selectedEntity.key ? (
                  <span className="rt-plans-fw__entity-key">{selectedEntity.key}</span>
                ) : null}
                {selectedEntity.status ? <StatusBadge status={selectedEntity.status} /> : null}
                {selectedEntity.priority ? <PriorityBadge priority={selectedEntity.priority} /> : null}
              </div>
              <div className="rt-plans-fw__entity-meta">
                {selectedEntity.start_date || selectedEntity.end_date ? (
                  <span>
                    {selectedEntity.start_date ? formatDate(selectedEntity.start_date) : '—'}
                    {' → '}
                    {selectedEntity.end_date ? formatDate(selectedEntity.end_date) : '—'}
                  </span>
                ) : null}
                {selectedEntity.assignee ? <span>Owner · {selectedEntity.assignee}</span> : null}
                {selectedEntity.description || selectedEntity.summary ? (
                  <span className="rt-plans-fw__entity-desc" title={selectedEntity.description || selectedEntity.summary}>
                    {String(selectedEntity.description || selectedEntity.summary).slice(0, 120)}
                    {String(selectedEntity.description || selectedEntity.summary).length > 120 ? '…' : ''}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="rt-plans-fw__entity-header-actions">
              <button
                type="button"
                className="rt-btn rt-btn--secondary rt-btn--sm"
                onClick={(e) => openEditEntity(selectedEntity, e)}
              >
                <LuPencil size={14} aria-hidden /> Edit {config.label}
              </button>
            </div>
          </header>

          {/* Metrics from loaded plans only */}
          <div className="rt-plans-fw__metrics">
            <MetricCard label="Plans" tone="primary" value={plansLoading ? '…' : plans.length} />
            <MetricCard label="Suites linked" tone="accent" value={plansLoading ? '…' : planMetrics.suiteCount} />
            <MetricCard label="Test cases" tone="violet" value={plansLoading ? '…' : planMetrics.caseCount} />
            <MetricCard
              label="Active / Draft"
              tone="success"
              value={plansLoading ? '…' : `${planMetrics.activeCount} / ${planMetrics.draftCount}`}
            />
          </div>

          {/* Full-width plans toolbar + table */}
          <div className="rt-plans-fw__toolbar">
            <FilterBar>
              <SearchInput value={planQuery} onChange={setPlanQuery} placeholder="Search plans…" />
              <div className="rt-plans-fw__toolbar-actions">
                <button
                  type="button"
                  className="rt-btn rt-btn--secondary rt-btn--sm"
                  onClick={() => navigateTo('schedules')}
                >
                  <LuCalendarClock size={14} aria-hidden /> Schedules
                </button>
                <button
                  type="button"
                  className="rt-btn rt-btn--secondary rt-btn--sm"
                  disabled={exporting}
                  onClick={handleExport}
                >
                  <LuDownload size={14} aria-hidden /> {exporting ? 'Exporting…' : 'Export'}
                </button>
                <button
                  type="button"
                  className="rt-btn rt-btn--primary rt-btn--sm"
                  onClick={() => setCreatePlanOpen(true)}
                >
                  <LuPlus size={14} aria-hidden /> Create plan
                </button>
              </div>
            </FilterBar>
          </div>

          <div className="rt-plans-fw__table">
            {plansLoading && <LoadingState label="Loading plans…" />}
            {!plansLoading && plansError && (
              <ErrorState title="Unable to load plans" description={plansError} onRetry={loadPlans} />
            )}
            {!plansLoading && !plansError && (
              <>
                <DataTable
                  columns={columns}
                  rows={pagedPlans}
                  rowKey="test_plan_id"
                  emptyTitle="No plans for this entity"
                  emptyDescription={`Create a plan linked to this ${config.singular}, then run or schedule it.`}
                />
                <Pagination
                  page={planPage}
                  pageSize={planPageSize}
                  total={filteredPlans.length}
                  onPageChange={setPlanPage}
                  onPageSizeChange={(size) => {
                    setPlanPageSize(size);
                    setPlanPage(1);
                  }}
                  pageSizeOptions={[5, 10, 20, 50]}
                  loading={plansLoading}
                />
              </>
            )}
          </div>
        </>
      )}

      <BulkActionBar count={selectedPlanIds.length} onClear={() => setSelectedPlanIds([])}>
        <button
          type="button"
          className="rt-btn rt-btn--primary rt-btn--sm"
          onClick={() => {
            const selected = plans.filter((p) => selectedPlanIds.includes(p.test_plan_id));
            openRunForPlans(selected);
          }}
        >
          <LuPlay size={14} aria-hidden /> Run selected
        </button>
      </BulkActionBar>

      {entityModalOpen && (
        <SprintModal
          onClose={() => {
            setEntityModalOpen(false);
            setEditingEntity(null);
          }}
          onSubmit={handleEntitySubmit}
          projectId={projectId}
          sprintData={editingEntity}
          isSubmitting={entitySubmitting}
          entityType={config.entityType}
        />
      )}

      {createPlanOpen && selectedEntity && (
        <CreateTestPlanModal
          onClose={() => setCreatePlanOpen(false)}
          onSave={handleCreatePlan}
          selectedEntity={selectedEntity}
          activeTab={config.activeTab}
          projectName={projectName}
          projectId={projectId}
          releaseId={selectedEntity.release_id || selectedEntity.id}
          initialEnvironments={Array.isArray(environments) ? environments : []}
          initialMembers={Array.isArray(members) ? members : []}
        />
      )}

      {viewPlan && (
        <ViewTestPlanModal
          testPlan={viewPlan}
          projectId={projectId}
          initialMembers={Array.isArray(members) ? members : []}
          onClose={() => {
            setViewPlan(null);
            setEditingPlan(false);
          }}
          onEdit={(updatedData) => handleEditPlan(viewPlan.test_plan_id, updatedData)}
          onDelete={() => setPlanToDelete(viewPlan)}
          onGenerateScenarios={() => {
            toast.info('Scenario generation is available from the plan detail page.');
          }}
          mode={editingPlan ? 'edit' : 'view'}
        />
      )}

      {runPlans && (
        <RunTestPlanModal
          isOpen
          onClose={() => setRunPlans(null)}
          testPlanIds={runPlans.ids}
          testPlanNames={runPlans.names}
          projectId={projectId}
          projectName={projectName}
          environmentSettingId={runPlans.environmentSettingId}
        />
      )}

      {schedulePlan && (
        <ScheduleTestPlanModal
          isOpen
          onClose={() => setSchedulePlan(null)}
          testPlanList={plans}
          initialPlan={schedulePlan}
          projectId={projectId}
          onFeedback={() => setSchedulePlan(null)}
        />
      )}

      {linkPlan && (
        <LinkTestSuitesModal
          isOpen
          onClose={() => setLinkPlan(null)}
          testPlan={linkPlan}
          projectId={projectId}
          mode={linkMode}
          onSuccess={() => {
            setLinkPlan(null);
            loadPlans();
          }}
        />
      )}

      <ExecutionHistoryModal
        isOpen={Boolean(historyPlan)}
        onClose={() => setHistoryPlan(null)}
        entityType="plan"
        entity={historyPlan}
      />

      <ConfirmDialog
        open={Boolean(entityToDelete)}
        title={`Delete ${config.singular}?`}
        body={`Delete "${entityToDelete ? config.getName(entityToDelete) : ''}"? Linked plans may be affected.`}
        confirmLabel={deletingEntity ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => setEntityToDelete(null)}
        onConfirm={confirmDeleteEntity}
      />

      <ConfirmDialog
        open={Boolean(planToDelete)}
        title="Delete plan?"
        body={`Delete "${planToDelete?.name}"? This cannot be undone.`}
        confirmLabel={deletingPlan ? 'Deleting…' : 'Delete'}
        danger
        onCancel={() => setPlanToDelete(null)}
        onConfirm={confirmDeletePlan}
      />
    </div>
  );
};

export default PlansEntityWorkspace;
